import { DecimalPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as PDActions from '../../../../store/actions/pd.actions';
import { selectProductPerformanceEventsLoading, selectSitePerformanceEventsLoading, selectViewMode } from '../../../../store/selectors/pd.selectors';

import { CookieService } from 'ngx-shared-services';
import { environment } from '../../../../environments/environment';
import { DataQuality, PastDispatchTableRow } from '../../../../models/performance-view.model';
import { ProgramOrganizationType } from '../../../../models/program-organization.model';
import { PaginationStore } from '../../../../store/signal-stores/pagination.signal-store';
import { ExportCSVService } from '../../../services/export-csv.service';
import { BaseCardComponent } from '../../../shared/components/base-card/base-card.component';
import { BaseTableComponent } from '../../../shared/components/base-table/base-table.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { TableStateComponent } from '../../../shared/components/table-state/table-state.component';
import { RoutesName } from '../../../shared/constants/routes-name';
import { LocaleDatePipe } from '../../../shared/pipes/locale-date.pipe';
import { getColor } from '../../../shared/utils/performance.utils';
import { ORGANIZATION_COLUMNS, PastDispatchColumnKey, PROGRAM_PRODUCT_COLUMNS } from './performance-details-table-config';
@Component({
  selector: 'fdr-past-performance-ui-performance-details',
  imports: [TableStateComponent, PaginationComponent, RouterModule, DecimalPipe, BaseCardComponent, TranslateModule, LocaleDatePipe, NgIf],
  providers: [PaginationStore],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './performance-details.component.html',
  styleUrls: ['./performance-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerformanceDetailsComponent extends BaseTableComponent<PastDispatchTableRow, PastDispatchColumnKey> {
  private readonly cookieService = inject(CookieService);
  readonly locale = this.cookieService.getI18NLocale() || environment.defaults.locale;
  private readonly exportCSVService = inject(ExportCSVService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly viewMode = this.store.selectSignal(selectViewMode);

  private readonly siteLoading = this.store.selectSignal(selectSitePerformanceEventsLoading);
  private readonly productLoading = this.store.selectSignal(selectProductPerformanceEventsLoading);

  readonly isLoading = computed(() => {
    if (this.viewMode() === ProgramOrganizationType.Program) {
      return this.productLoading();
    }
    return this.siteLoading();
  });

  constructor() {
    const columnsForView = computed(() => {
      if (this.viewMode() !== ProgramOrganizationType.Program) {
        return ORGANIZATION_COLUMNS;
      }
      return PROGRAM_PRODUCT_COLUMNS;
    });

    super(columnsForView);
  }

  protected exportCSV() {
    this.exportCSVService.exportFromTable(this.rows(), this.columnsSignal(), 'PastDispatches', this.locale);
  }

  navigateToDetail(row: PastDispatchTableRow) {
    const isOrg = this.viewMode() === ProgramOrganizationType.Organization;
    this.store.dispatch(
      PDActions.selectPerformanceEvent({ kind: isOrg ? 'site' : 'product', eventId: row.id, siteId: row.siteId, productId: row.productId })
    );
    if (isOrg) {
      this.router.navigate([RoutesName.ORGANIZATION, RoutesName.EVENT_DETAILS], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    } else {
      this.router.navigate([RoutesName.PROGRAM, RoutesName.PORTFOLIO, row.productId], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    }
  }

  getChipColor(name: string): string {
    return getColor(name);
  }

  getDataQualityTranslationKey(dataQuality: DataQuality): string {
    if (dataQuality === 'Final') {
      return 'PD.TABLE.FINAL';
    }
    return 'PD.TABLE.PRELIMINARY';
  }

}
